function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
var _abortController = _classPrivateFieldLooseKey("abortController");
var _cachedContent = _classPrivateFieldLooseKey("cachedContent");
var _cartUpdateAbortController = _classPrivateFieldLooseKey("cartUpdateAbortController");
var _getSelectedVariantId = _classPrivateFieldLooseKey("getSelectedVariantId");
var _handleCartUpdate = _classPrivateFieldLooseKey("handleCartUpdate");
var _updateVariantPicker = _classPrivateFieldLooseKey("updateVariantPicker");
var _resetScroll = _classPrivateFieldLooseKey("resetScroll");
var _stayVisibleUntilDialogCloses = _classPrivateFieldLooseKey("stayVisibleUntilDialogCloses");
var _openQuickAddModal = _classPrivateFieldLooseKey("openQuickAddModal");
var _closeQuickAddModal = _classPrivateFieldLooseKey("closeQuickAddModal");
var _updateQuickAddButtonState = _classPrivateFieldLooseKey("updateQuickAddButtonState");
var _syncVariantSelection = _classPrivateFieldLooseKey("syncVariantSelection");
export class QuickAddComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Syncs the variant selection from the product card to the modal
     * @param {Element} modalContent - The modal content element
     */
    Object.defineProperty(this, _syncVariantSelection, {
      value: _syncVariantSelection2
    });
    /**
     * Updates the quick-add button state based on whether a swatch is selected
     * @param {VariantSelectedEvent} event - The variant selected event
     */
    Object.defineProperty(this, _updateQuickAddButtonState, {
      value: _updateQuickAddButtonState2
    });
    /** @param {QuickAddDialog} dialogComponent */
    Object.defineProperty(this, _stayVisibleUntilDialogCloses, {
      value: _stayVisibleUntilDialogCloses2
    });
    Object.defineProperty(this, _resetScroll, {
      value: _resetScroll2
    });
    /**
     * Re-renders the variant picker in the quick-add modal.
     * @param {Element} newHtml - The element to re-render.
     */
    Object.defineProperty(this, _updateVariantPicker, {
      value: _updateVariantPicker2
    });
    /**
     * Gets the currently selected variant ID from the product card
     * @returns {string | null} The variant ID or null
     */
    Object.defineProperty(this, _getSelectedVariantId, {
      value: _getSelectedVariantId2
    });
    /** @type {AbortController | null} */
    Object.defineProperty(this, _abortController, {
      writable: true,
      value: null
    });
    /** @type {Map<string, Element>} */
    Object.defineProperty(this, _cachedContent, {
      writable: true,
      value: new Map()
    });
    /** @type {AbortController} */
    Object.defineProperty(this, _cartUpdateAbortController, {
      writable: true,
      value: new AbortController()
    });
    /**
     * Clears the cached content when cart is updated
     */
    Object.defineProperty(this, _handleCartUpdate, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _cachedContent)[_cachedContent].clear();
      }
    });
    /**
     * Handles quick add button click
     * @param {Event} event - The click event
     */
    this.handleClick = async event => {
      event.preventDefault();
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = _classPrivateFieldLooseBase(this, _cachedContent)[_cachedContent].get(currentUrl);
      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */gridElement.cloneNode(true);
            _classPrivateFieldLooseBase(this, _cachedContent)[_cachedContent].set(currentUrl, productGrid);
          }
        }
      }
      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */productGrid.cloneNode(true);
        await this.updateQuickAddModal(freshContent);
        _classPrivateFieldLooseBase(this, _updateVariantPicker)[_updateVariantPicker](productGrid);
      }
      _classPrivateFieldLooseBase(this, _openQuickAddModal)[_openQuickAddModal]();
    };
    Object.defineProperty(this, _openQuickAddModal, {
      writable: true,
      value: () => {
        const dialogComponent = document.getElementById('quick-add-dialog');
        if (!(dialogComponent instanceof QuickAddDialog)) return;
        _classPrivateFieldLooseBase(this, _stayVisibleUntilDialogCloses)[_stayVisibleUntilDialogCloses](dialogComponent);
        dialogComponent.showDialog();

        // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
        // Waiting until the open animation starts seemed to be the most reliable metric here.
        const dialog = dialogComponent.refs?.dialog;
        if (!dialog) return;
        dialog.addEventListener('animationstart', _classPrivateFieldLooseBase(this, _resetScroll)[_resetScroll].bind(this), {
          once: true
        });
      }
    });
    Object.defineProperty(this, _closeQuickAddModal, {
      writable: true,
      value: () => {
        const dialogComponent = document.getElementById('quick-add-dialog');
        if (!(dialogComponent instanceof QuickAddDialog)) return;
        dialogComponent.closeDialog();
      }
    });
  }
  get productPageUrl() {
    const productUrlFromData = this.dataset.productUrl;
    if (productUrlFromData) {
      return productUrlFromData;
    }
    const productCard = /** @type {import('./product-card').ProductCard | null} */this.closest('product-card');
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */
    this.closest('product-hotspot-component');
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();
    if (!productLink?.href) return '';
    const url = new URL(productLink.href);
    if (url.searchParams.has('variant')) {
      return url.toString();
    }
    const selectedVariantId = _classPrivateFieldLooseBase(this, _getSelectedVariantId)[_getSelectedVariantId]();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }
    return url.toString();
  }
  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', _classPrivateFieldLooseBase(this, _closeQuickAddModal)[_closeQuickAddModal]);
    document.addEventListener(ThemeEvents.cartUpdate, _classPrivateFieldLooseBase(this, _handleCartUpdate)[_handleCartUpdate], {
      signal: _classPrivateFieldLooseBase(this, _cartUpdateAbortController)[_cartUpdateAbortController].signal
    });
    document.addEventListener(ThemeEvents.variantSelected, _classPrivateFieldLooseBase(this, _updateQuickAddButtonState)[_updateQuickAddButtonState].bind(this));
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', _classPrivateFieldLooseBase(this, _closeQuickAddModal)[_closeQuickAddModal]);
    _classPrivateFieldLooseBase(this, _abortController)[_abortController]?.abort();
    _classPrivateFieldLooseBase(this, _cartUpdateAbortController)[_cartUpdateAbortController].abort();
    document.removeEventListener(ThemeEvents.variantSelected, _classPrivateFieldLooseBase(this, _updateQuickAddButtonState)[_updateQuickAddButtonState].bind(this));
  }
  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    _classPrivateFieldLooseBase(this, _abortController)[_abortController]?.abort();
    _classPrivateFieldLooseBase(this, _abortController)[_abortController] = new AbortController();
    try {
      const response = await fetch(productPageUrl, {
        signal: _classPrivateFieldLooseBase(this, _abortController)[_abortController].signal
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      _classPrivateFieldLooseBase(this, _abortController)[_abortController] = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;
    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;
      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);
      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }
      productDetails?.remove();
    }
    morph(modalContent, productGrid);
    _classPrivateFieldLooseBase(this, _syncVariantSelection)[_syncVariantSelection](modalContent);
  }
}
function _getSelectedVariantId2() {
  const productCard = /** @type {import('./product-card').ProductCard | null} */this.closest('product-card');
  return productCard?.getSelectedVariantId() || null;
}
function _updateVariantPicker2(newHtml) {
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;
  const variantPicker = /** @type {VariantPicker} */modalContent.querySelector('variant-picker');
  variantPicker.updateVariantPicker(newHtml);
}
function _resetScroll2() {
  const dialogComponent = document.getElementById('quick-add-dialog');
  if (!(dialogComponent instanceof QuickAddDialog)) return;
  const productDetails = dialogComponent.querySelector('.product-details');
  const productMedia = dialogComponent.querySelector('.product-information__media');
  productDetails?.scrollTo({
    top: 0,
    behavior: 'instant'
  });
  productMedia?.scrollTo({
    top: 0,
    behavior: 'instant'
  });
}
function _stayVisibleUntilDialogCloses2(dialogComponent) {
  this.toggleAttribute('stay-visible', true);
  dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
    once: true
  });
}
function _updateQuickAddButtonState2(event) {
  if (!(event.target instanceof HTMLElement)) return;
  if (event.target.closest('product-card') !== this.closest('product-card')) return;
  const productOptionsCount = this.dataset.productOptionsCount;
  const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
  this.setAttribute('data-quick-add-button', quickAddButton);
}
function _syncVariantSelection2(modalContent) {
  const selectedVariantId = _classPrivateFieldLooseBase(this, _getSelectedVariantId)[_getSelectedVariantId]();
  if (!selectedVariantId) return;

  // Find and check the corresponding input in the modal
  const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
  for (const input of modalInputs) {
    if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', {
        bubbles: true
      }));
      break;
    }
  }
}
if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}
var _abortController2 = _classPrivateFieldLooseKey("abortController");
var _updateProductTitleLink = _classPrivateFieldLooseKey("updateProductTitleLink");
var _handleDialogClose = _classPrivateFieldLooseKey("handleDialogClose");
class QuickAddDialog extends DialogComponent {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _abortController2, {
      writable: true,
      value: new AbortController()
    });
    /**
     * Closes the dialog
     * @param {CartUpdateEvent} event - The cart update event
     */
    this.handleCartUpdate = event => {
      if (event.detail.data.didError) return;
      this.closeDialog();
    };
    Object.defineProperty(this, _updateProductTitleLink, {
      writable: true,
      value: (/** @type {CustomEvent} */event) => {
        const anchorElement = /** @type {HTMLAnchorElement} */
        event.detail.data.html?.querySelector('.view-product-title a');
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */this.querySelector('.view-product-title a');
        const mobileProductTitle = /** @type {HTMLAnchorElement} */this.querySelector('.product-header a');
        if (!anchorElement) return;
        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      }
    });
    Object.defineProperty(this, _handleDialogClose, {
      writable: true,
      value: () => {
        const iosVersion = getIOSVersion();
        /**
         * This is a patch to solve an issue with the UI freezing when the dialog is closed.
         * To reproduce it, use iOS 16.0.
         */
        if (!iosVersion || iosVersion.major >= 17 || iosVersion.major === 16 && iosVersion.minor >= 4) return;
        requestAnimationFrame(() => {
          /** @type {HTMLElement | null} */
          const grid = document.querySelector('#ResultsList [product-grid-view]');
          if (grid) {
            const currentWidth = grid.getBoundingClientRect().width;
            grid.style.width = `${currentWidth - 1}px`;
            requestAnimationFrame(() => {
              grid.style.width = '';
            });
          }
        });
      }
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, {
      signal: _classPrivateFieldLooseBase(this, _abortController2)[_abortController2].signal
    });
    this.addEventListener(ThemeEvents.variantUpdate, _classPrivateFieldLooseBase(this, _updateProductTitleLink)[_updateProductTitleLink]);
    this.addEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _handleDialogClose)[_handleDialogClose]);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _abortController2)[_abortController2].abort();
    this.removeEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _handleDialogClose)[_handleDialogClose]);
  }
}
if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
